import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import * as yup from 'yup'
import { Box, Container, GlobalStyles, TextField, Typography } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import type { PayloadPushNotificationProps } from 'modules/partial'
import {
  buildSegmentationJson,
  SEGMENTATION_DELETE_FIELDS,
  pushNotificationSegmentationSchemaShape,
  targetOptionsSchema
} from 'components/segmentation/utils'
import SegmentationFormFields from 'components/segmentation'
import Loading from 'components/loading/loading'
import Dialog from 'components/dialog/dialog'
import BackdropImage from 'components/backdropImage/backdropImage'
import DialogStyle from './style'

interface PushNotificationFormProps {
  initialValues: PayloadPushNotificationProps
  onSubmit: (values: FormData) => void
  isEdit?: boolean
  detailLoading?: boolean
  title: string
  open: boolean
  onCancel: () => void
  loading?: boolean
  submitText?: string
}

const PlaceholderHelperText = () => (
  <Box sx={{ mt: -1.5 }}>
    <Typography variant='caption' color='text.secondary'>
      You can use these placeholders in the field above:
    </Typography>
    <Box component='ul' sx={{ mt: 0.5, mb: 0, pl: 2 }}>
      <Typography component='li' variant='caption' color='text.secondary'>
        {'{CarModel}'} Car Model of customer
      </Typography>
      <Typography component='li' variant='caption' color='text.secondary'>
        {'{ModelYear}'} Car Model Year of customer
      </Typography>
      <Typography component='li' variant='caption' color='text.secondary'>
        {'{City}'} City of customer
      </Typography>
      <Typography component='li' variant='caption' color='text.secondary'>
        {'{CarType}'} Car Type of customer
      </Typography>
      <Typography component='li' variant='caption' color='text.secondary'>
        {'{UserName}'} Full name of customer
      </Typography>
    </Box>
  </Box>
)

const scheme = yup.object().shape({
  id: yup.string().when('$isEdit', {
    is: true,
    then: (schema) => schema.min(1).required('Id is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  name: yup.string().required('Push Notification Name is required'),
  airingTime: yup.string().required('Airing Time is required'),
  headline: yup
    .string()
    .required('Headline is required')
    .max(100, 'Headline has a max length of 100'),
  subHeadline: yup
    .string()
    .required('SubHeadline is required')
    .max(200, 'SubHeadline has a max length of 200'),
  ctaUrl: yup.string().max(300, 'Cta Url has a max length of 300'),
  image: yup
    .mixed<File>()
    .nullable()
    .test('fileSize', 'Exceed maximum file sixe allowed (50kb)', (value) => {
      if (!value) return true
      return value.size <= 50 * 1024
    })
    .test('fileType', 'Format should be in jpg/png', (value) => {
      if (!value) return true
      return ['image/jpeg', 'image/png'].includes(value.type)
    }),
  ...pushNotificationSegmentationSchemaShape,
  targetOptions: targetOptionsSchema.min(1, 'Target Options is required')
})

const PushNotificationForm = ({
  initialValues,
  onSubmit,
  isEdit,
  detailLoading,
  title,
  open,
  onCancel,
  loading,
  submitText
}: PushNotificationFormProps) => {
  const [airingTime, setAiringTime] = useState<Dayjs | null>(null)
  const [previewImage, setPreviewImage] = useState<string>('')
  const [previewBackdrop, setPreviewBackdrop] = useState<boolean>(false)

  const handleSubmit = (values: PayloadPushNotificationProps) => {
    const data: PayloadPushNotificationProps = {
      ...values,
      targetOptions: Array.isArray(values.targetOptions)
        ? (values.targetOptions.join(', ') as string)
        : values.targetOptions,
      segmentationJson: buildSegmentationJson(values)
    }

    SEGMENTATION_DELETE_FIELDS.forEach((field) => delete (data as never)[field])
    delete (data as never)['image']

    const formData = new FormData()
    if (values.vinCsvFile) {
      formData.set('file', values.vinCsvFile)
    }
    if (values.memberIdCsvFile) {
      formData.set('file', values.memberIdCsvFile)
    }
    if (values.image) {
      formData.set('image', values.image)
    }

    formData.set('data', JSON.stringify(data))

    onSubmit(formData)
  }

  useEffect(() => {
    if (isEdit && initialValues.airingTime) {
      setAiringTime(dayjs(initialValues.airingTime))
    }
    if (initialValues.imageUrl) {
      setPreviewImage(initialValues.imageUrl)
    }
  }, [initialValues, isEdit])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={scheme}
      validationContext={{
        isEdit,
        airingTime: airingTime ? airingTime.format('YYYY/MM/DD HH:mm:ss') : undefined
      }}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleBlur, isValid, submitForm }) => {
        const handleChangeAiringTime = (date: Dayjs | null) => {
          setAiringTime(date)
          const newDate = (!isEmpty(date) && date.format('YYYY/MM/DD HH:mm:ss')) || ''
          setFieldValue('airingTime', newDate)
        }

        return (
          <Dialog
            title={title}
            open={open}
            onCancel={onCancel}
            onSubmit={submitForm}
            loading={!!loading}
            isDisabled={!isValid}
            maxWidth='md'
            submitText={submitText}
          >
            <Container {...DialogStyle.Container}>
              {detailLoading && <Loading />}
              {!detailLoading && (
                <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                  <TextField
                    id='name'
                    name='name'
                    variant='outlined'
                    label='Push Notification Name'
                    value={values.name}
                    onChange={(event) => setFieldValue('name', event.target.value)}
                    onBlur={handleBlur}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    required
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                    <GlobalStyles
                      styles={{
                        '.MuiMultiSectionDigitalClock-root': {
                          scrollbarWidth: 'none !important',
                          msOverflowStyle: 'none !important'
                        },
                        '.MuiMultiSectionDigitalClock-root::-webkit-scrollbar': {
                          display: 'none !important'
                        }
                      }}
                    />
                    <DateTimePicker
                      value={airingTime}
                      minDate={dayjs()}
                      onChange={handleChangeAiringTime}
                      label='Airing Time'
                      format='DD-MM-YYYY HH:mm'
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          name: 'airingTime',
                          onBlur: handleBlur,
                          error: touched.airingTime && Boolean(errors.airingTime),
                          helperText: touched.airingTime && errors.airingTime,
                          required: true,
                          fullWidth: true
                        },
                        popper: {
                          sx: {
                            '& .MuiMultiSectionDigitalClock-root': {
                              scrollbarWidth: 'none',
                              msOverflowStyle: 'none',
                              '&::-webkit-scrollbar': {
                                display: 'none'
                              }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                  <SegmentationFormFields required restrictMemberTierCombinations />
                  <TextField
                    id='headline'
                    variant='outlined'
                    label='Headline'
                    value={values.headline}
                    onChange={(event) => setFieldValue('headline', event.target.value)}
                    onBlur={handleBlur}
                    error={
                      touched && Boolean(touched.headline) && errors && Boolean(errors.headline)
                    }
                    helperText={touched && touched.headline && errors && errors.headline}
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    required
                  />
                  <PlaceholderHelperText />
                  <TextField
                    id='subHeadline'
                    variant='outlined'
                    label='SubHeadline'
                    value={values.subHeadline}
                    onChange={(event) => setFieldValue('subHeadline', event.target.value)}
                    onBlur={handleBlur}
                    error={
                      touched &&
                      Boolean(touched.subHeadline) &&
                      errors &&
                      Boolean(errors.subHeadline)
                    }
                    helperText={touched && touched.subHeadline && errors && errors.subHeadline}
                    inputProps={{ maxLength: 200 }}
                    fullWidth
                    required
                  />
                  <PlaceholderHelperText />
                  <TextField
                    id='ctaUrl'
                    variant='outlined'
                    label='CTA Url'
                    value={values.ctaUrl}
                    onChange={(event) => setFieldValue('ctaUrl', event.target.value)}
                    onBlur={handleBlur}
                    error={touched && Boolean(touched.ctaUrl) && errors && Boolean(errors.ctaUrl)}
                    helperText={touched && touched.ctaUrl && errors && errors.ctaUrl}
                    inputProps={{ maxLength: 300 }}
                    fullWidth
                  />
                  <TextField
                    id='image'
                    name='image'
                    label='Upload Image'
                    type='file'
                    placeholder='Upload Image'
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ accept: 'image/png, image/jpeg' }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const files = event.target.files
                      if (files && files.length > 0 && !isEmpty(files)) {
                        setFieldValue('image', files[0])
                        const reader = new FileReader()
                        reader.onload = () => {
                          setPreviewImage(reader.result as string)
                        }
                        reader.readAsDataURL(files[0])
                      } else {
                        setFieldValue('image', null)
                        setPreviewImage('')
                      }
                    }}
                    onBlur={handleBlur}
                    error={touched && Boolean(touched.image) && errors && Boolean(errors.image)}
                    helperText={touched && touched.image && errors && errors.image}
                    fullWidth
                  />
                  {previewImage && (
                    <Box {...DialogStyle.Image}>
                      <BackdropImage
                        open={previewBackdrop}
                        onOpen={() => setPreviewBackdrop(true)}
                        onClose={() => setPreviewBackdrop(false)}
                      >
                        <img
                          src={previewImage}
                          alt=''
                          style={{ height: '300px', width: '505px', objectFit: 'contain' }}
                        />
                      </BackdropImage>
                    </Box>
                  )}
                </Box>
              )}
            </Container>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default PushNotificationForm
